import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

export const DarkModeProvider = ({ children, isAuthenticated }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyDarkMode = useCallback((enabled) => {
    setDarkMode(enabled);
    document.body.classList.toggle('dark-mode', enabled);
    localStorage.setItem('darkMode', enabled);
  }, []);

  const refreshDarkMode = useCallback(async () => {
    try {
      const res = await axios.get('https://demo.insollex.com/api/settings', {
        withCredentials: true,
      });
      const serverDark = res.data.darkMode === true || res.data.darkMode === 'true';
      applyDarkMode(serverDark);
    } catch {
      const stored = localStorage.getItem('darkMode') === 'true';
      applyDarkMode(stored);
    } finally {
      setLoading(false);
    }
  }, [applyDarkMode]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshDarkMode();
    } else {
      // User not authenticated, apply local storage or default mode
      const stored = localStorage.getItem('darkMode') === 'true';
      applyDarkMode(stored);
      setLoading(false);
    }
  }, [isAuthenticated, refreshDarkMode, applyDarkMode]);

  const toggleDarkMode = useCallback(async (enabled) => {
    const newMode = typeof enabled === 'boolean' ? enabled : !darkMode;
    applyDarkMode(newMode);

    if (!isAuthenticated) {
      // Optionally skip API call if not authenticated
      return;
    }

    try {
      await axios.post(
        'https://demo.insollex.com/api/settings',
        { darkMode: newMode },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Failed to save dark mode to server:', err);
    }
  }, [darkMode, applyDarkMode, isAuthenticated]);

  const contextValue = useMemo(() => ({
    darkMode,
    toggleDarkMode,
    refreshDarkMode,
    loading,
  }), [darkMode, toggleDarkMode, refreshDarkMode]);

  return (
    <DarkModeContext.Provider value={contextValue}>
      {children}
    </DarkModeContext.Provider>
  );
};
